import React, { useState } from "react";
import {
  Button,
  Checkbox,
  FormControlLabel,
  Menu,
  Rating,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { filterMenuSlotProps } from "./filterMenuStyles.js";

const MIN_RATING = 0.5;
const MAX_RATING = 5;

const MODE_LABELS = {
  exactly: "Exactly",
  atLeast: "At least",
  atMost: "At most",
};

/**
 * Read a rating filter's URL token (see _apply_rating_filter in filters.py)
 * as { unrated, mode, rating }. A range is shown as "at least" when it runs
 * to 5 and "at most" when it starts at 0.5 — the only ranges this menu
 * writes. Any other range (only reachable by editing the URL) opens as
 * "at least" its lower bound.
 */
export const parseRatingFilter = (token) => {
  if (!token) return { unrated: false, mode: "exactly", rating: null };
  if (token === "none") return { unrated: true, mode: "exactly", rating: null };
  const [low, high] = token.split("-").map(Number);
  if (high === undefined || low === high) {
    return { unrated: false, mode: "exactly", rating: low };
  }
  if (low === MIN_RATING)
    return { unrated: false, mode: "atMost", rating: high };
  return { unrated: false, mode: "atLeast", rating: low };
};

const serialiseRatingFilter = ({ unrated, mode, rating }) => {
  if (unrated) return "none";
  // "At least 5" / "at most 0.5" are just exact ratings
  if (mode === "atLeast" && rating < MAX_RATING)
    return `${rating}-${MAX_RATING}`;
  if (mode === "atMost" && rating > MIN_RATING)
    return `${MIN_RATING}-${rating}`;
  return String(rating);
};

/** What the filter's chip says, e.g. "4", "At least 3.5", "Unrated" */
export const formatRatingFilter = (token) => {
  const parsed = parseRatingFilter(token);
  // A range the menu can't express (see parseRatingFilter) is shown as is
  if (serialiseRatingFilter(parsed) !== token) return token.replace("-", "–");
  if (parsed.unrated) return "Unrated";
  return parsed.mode === "exactly"
    ? String(parsed.rating)
    : `${MODE_LABELS[parsed.mode]} ${parsed.rating}`;
};

/**
 * Track's rating filter: pick how to compare (exactly / at least / at most)
 * and a star rating — or "Unrated only" instead. Nothing applies until Apply.
 *
 * @param {string|null} value - The filter's current URL token, if set
 * @param {(token: string) => void} onApply
 * @param {() => void} onClear
 */
const RatingFilterMenu = ({ value, onApply, onClear, anchorEl, onClose }) => {
  // Mounted fresh each time the menu opens, so this starts from the URL
  const [draft, setDraft] = useState(() => parseRatingFilter(value));

  const canApply = draft.unrated || draft.rating !== null;

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      slotProps={filterMenuSlotProps}
    >
      {/* One Stack, so every section sits the same distance apart */}
      <Stack spacing={2} sx={{ px: 2, py: 1.5 }}>
        <ToggleButtonGroup
          value={draft.mode}
          exclusive
          size="small"
          fullWidth
          disabled={draft.unrated}
          // Deselecting the active mode reports null — keep it selected
          onChange={(event, mode) => mode && setDraft({ ...draft, mode })}
        >
          {Object.entries(MODE_LABELS).map(([mode, label]) => (
            <ToggleButton
              key={mode}
              value={mode}
              sx={{ textTransform: "none", whiteSpace: "nowrap" }}
            >
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Rating
          name="rating-filter"
          value={draft.rating}
          precision={0.5}
          size="large"
          disabled={draft.unrated}
          onChange={(event, rating) => setDraft({ ...draft, rating })}
          sx={{ alignSelf: "center" }}
        />

        <FormControlLabel
          sx={{ m: 0 }}
          control={
            <Checkbox
              sx={{ p: 0, mr: 1 }}
              checked={draft.unrated}
              onChange={(event) =>
                setDraft({ ...draft, unrated: event.target.checked })
              }
            />
          }
          label="Unrated only"
        />

        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <Button variant="outlined" size="small" onClick={onClear}>
            Clear
          </Button>
          <Button
            variant="contained"
            size="small"
            disabled={!canApply}
            onClick={() => onApply(serialiseRatingFilter(draft))}
          >
            Apply
          </Button>
        </Stack>
      </Stack>
    </Menu>
  );
};

export default RatingFilterMenu;
